import {Injectable, Renderer2, RendererFactory2, RendererStyleFlags2} from '@angular/core';

enum CssUnits {
  PX = 'px',
  REM = 'rem'
}

@Injectable({providedIn: 'root'})
export class CssVariablesService {
  private renderer: Renderer2;

  readonly HEADER_PX: number = this.getAsPx('--main-container-padding');
  readonly MAIN_CONTAINER_PADDING_PX: number = this.getAsPx('--main-container-padding');
  readonly PRIMARY_COLOR: string = this.getValue('--bs-primary');
  readonly RED_COLOR: string = this.getValue('--bs-red');

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  getAsPx(cssPropertyName: string): number {
    const cssValue = this.getValue(cssPropertyName);
    return this.convert(cssValue, CssUnits.PX);
  }

  public getValue(cssPropertyName: string, element?: Element): string {
    return this.getComputedStyle(element).getPropertyValue(cssPropertyName);
  }

  public setProperty(element: Element, cssPropertyName: string, value: string): void {
    const flags: RendererStyleFlags2 = RendererStyleFlags2.DashCase;
    this.renderer.setStyle(element, cssPropertyName, value, flags);
  }

  private convert(value: string, toUnit: string): number {
    const [number, fromUnit] = parseIntoNumberAndUnitTuple(value) ?? [];
    const fromConverter = getConverter(fromUnit);
    const toConverter = getConverter(toUnit);
    return toConverter.toUnit(fromConverter.toBase(number));
  }

  private getComputedStyle(element?: Element): CSSStyleDeclaration {
    return getComputedStyle(element ?? document.documentElement);
  }
}

function parseIntoNumberAndUnitTuple (cssValue: string): [number, string] {
  const matches = cssValue.match(/(-?[\d.]+)([a-z%]*)/);
  if (matches?.length === 3) {
    return [+matches[1], matches[2]];
  } else {
    return null;
  }
};

function getConverter(unit: string): CssUnitConverter {
  switch (unit) {
    case CssUnits.PX:
      return pixelUnitConverter;
    case CssUnits.REM:
      return remUnitConverter;
    default:
      throw new Error('Css unit converter not found for: ' + unit);
  }
}

interface CssUnitConverter {
  /** convert the unit to base unit. Base unit is pixel. */
  toBase: (x: number) => number;
  toUnit: (x: number) => number;
}

const pixelUnitConverter: CssUnitConverter = {
  toBase: (x: number): number => x,
  toUnit: (x: number): number => x
};

const pxInRem: number = parseIntoNumberAndUnitTuple(getComputedStyle(document.documentElement).getPropertyValue('font-size'))[0];
const remUnitConverter: CssUnitConverter = {
  toBase: (x: number): number => x * pxInRem,
  toUnit: (x: number): number => x / pxInRem
};
